import { useMemo, useState } from 'react';
import {
  FileText,
  Download,
  Loader2,
  Eye,
  RotateCcw,
  Clock,
  Zap,
  Search,
  SearchX,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Report } from '../types';

function formatResponseTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function monthYearKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthYear(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

interface ReportHistoryProps {
  reports: Report[];
  onPreview: (report: Report) => void;
  onDownload: (report: Report) => void;
  onRerun: (report: Report) => void;
  onDelete: (report: Report) => void;
  isDeleting?: boolean;
}

const statusColors: Record<Report['status'], string> = {
  draft: 'bg-muted text-muted-foreground',
  generating: 'bg-yellow-500/10 text-yellow-600',
  ready: 'bg-green-500/10 text-green-600',
};

function ReportCard({
  report,
  onPreview,
  onDownload,
  onRerun,
  onRequestDelete,
  isDeleting,
}: {
  report: Report;
  onPreview: (report: Report) => void;
  onDownload: (report: Report) => void;
  onRerun: (report: Report) => void;
  onRequestDelete: (report: Report) => void;
  isDeleting?: boolean;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onPreview(report)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPreview(report);
        }
      }}
      className="group flex cursor-pointer flex-col gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-accent/50"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        {report.status === 'generating' ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : (
          <FileText className="h-5 w-5 text-primary" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium leading-snug">{report.title}</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge variant="secondary" className={cn('text-[10px]', statusColors[report.status])}>
            {report.status}
          </Badge>
          <span className="text-xs text-muted-foreground" title={report.createdAt.toLocaleString()}>
            {report.createdAt.toLocaleString([], {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </span>
        </div>
        {(report.tokens != null && report.tokens > 0) ||
        (report.responseTimeMs != null && report.responseTimeMs > 0) ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
            {report.status === 'ready' && report.tokens != null && report.tokens > 0 && (
              <span className="flex items-center gap-0.5" title="Tokens">
                <Zap className="h-3 w-3" />
                {formatTokens(report.tokens)}
              </span>
            )}
            {report.status === 'ready' &&
              report.responseTimeMs != null &&
              report.responseTimeMs > 0 && (
                <span className="flex items-center gap-0.5" title="Time taken">
                  <Clock className="h-3 w-3" />
                  {formatResponseTime(report.responseTimeMs)}
                </span>
              )}
          </div>
        ) : null}
      </div>

      {report.status !== 'generating' && (
        <div
          className="mt-auto flex items-center justify-between gap-2 border-t pt-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title="Delete report"
            disabled={isDeleting}
            onClick={() => onRequestDelete(report)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {report.status === 'ready' && (
            <div className="flex shrink-0 gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Rerun report"
                onClick={() => onRerun(report)}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Preview report"
                onClick={() => onPreview(report)}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Download report"
                onClick={() => onDownload(report)}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ReportHistory({
  reports,
  onPreview,
  onDownload,
  onRerun,
  onDelete,
  isDeleting = false,
}: ReportHistoryProps) {
  const [search, setSearch] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Report | null>(null);

  const filteredReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return reports;
    return reports.filter(
      (report) =>
        report.title.toLowerCase().includes(query) || report.type.toLowerCase().includes(query)
    );
  }, [reports, search]);

  const groupedReports = useMemo(() => {
    const groups = new Map<string, { label: string; reports: Report[] }>();

    for (const report of filteredReports) {
      const key = monthYearKey(report.createdAt);
      const existing = groups.get(key);
      if (existing) {
        existing.reports.push(report);
      } else {
        groups.set(key, { label: formatMonthYear(report.createdAt), reports: [report] });
      }
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, group]) => ({
        key,
        label: group.label,
        reports: group.reports,
      }));
  }, [filteredReports]);

  if (reports.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="text-center">
          <FileText className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">No reports yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b px-4 py-3">
        <div className="relative w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reports..."
            className="pl-9"
            aria-label="Search report history"
          />
        </div>
      </div>

      {filteredReports.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
          <SearchX className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">No reports match your search</p>
          <p className="text-xs text-muted-foreground">
            Try a different title or clear the search.
          </p>
        </div>
      ) : (
        <ScrollArea className="min-h-0 flex-1">
          <div className="flex flex-col gap-6 p-4">
            {groupedReports.map((group) => (
              <section key={group.key}>
                <h3 className="mb-3 text-sm font-semibold text-foreground">{group.label}</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {group.reports.map((report) => (
                    <ReportCard
                      key={report.id}
                      report={report}
                      onPreview={onPreview}
                      onDownload={onDownload}
                      onRerun={onRerun}
                      onRequestDelete={setPendingDelete}
                      isDeleting={isDeleting}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </ScrollArea>
      )}

      <Dialog
        open={pendingDelete != null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete report?</DialogTitle>
            <DialogDescription>
              {pendingDelete
                ? `"${pendingDelete.title}" will be removed from your history.`
                : 'This report will be removed from your history.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting || pendingDelete == null}
              onClick={() => {
                if (!pendingDelete) return;
                onDelete(pendingDelete);
                setPendingDelete(null);
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
